
import FullScreenDialog, { FullScreenDialogCloseReason } from '../full-screen-dialog/FullScreenDialog';
import SettingsBar, { SettingsActionEnum } from '../settings-bar/SettingsBar';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import UnsavedChangesPrompt from '../unsaved-changes-prompt/UnsavedChangesPrompt';
import { useConfirmation } from '../../contexts/confirmation-context/ConfirmationContext';
import ManualEntryForm from '../manual-entry-form/ManualEntryForm';
import { ManualEntryFormMode, ManualEntryFormRefType, ManualEntryFormStateType } from '../manual-entry-form/ManualEntryFormUtils';
import { ITagValue } from '../../common/models/ITagValue';
import { BindDialogReturn, DialogProps, DialogResult, DialogState, bindDialog, useDialogState } from '../../hooks/use-dialog-state/UseDialogState';
import { ViewOptionStateEnum } from '../contextual-app-bar/ViewOptionStateEnum';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import { AppSettingsActionTypeEnum, useAppSettings } from '../../contexts/app-context/AppContext';
import { Tune } from '@mui/icons-material';
import { IeLynxObject } from '../../common/models/eLynxObject';
import { IEntityValueTagModel } from '../../common/models/IEntityValueModels';
import { isMobile, isTablet } from 'react-device-detect';
import DesktopManualEntryForm from '../manual-entry-form/DesktopManualEntryForm';

export type WriteDownFormDialogInitValues = {
    objectName: string;
    writedownMode?: boolean;
    tags?: IEntityValueTagModel[];
    configurationID?: number; // Optional, used for write down configuration

    elynxObject?: IeLynxObject
};

export type WriteDownFormDialogResultValues = {
   newValues: ITagValue[];
   tags: IEntityValueTagModel[];
};

export type WriteDownFormDialogResultReason = FullScreenDialogCloseReason | "cancel" | "save";

export type WriteDownFormDialogResult = DialogResult<WriteDownFormDialogResultValues, WriteDownFormDialogResultReason>;

export type WriteDownFormDialogProps = DialogProps<WriteDownFormDialogInitValues, WriteDownFormDialogResult>;


export default function WriteDownFormDialog({
    open, 
    values,
    onClose,
}: WriteDownFormDialogProps) {
    
    const isPhone = isMobile && !isTablet;

    const [viewOptionState, setViewOptionState] = useState<ViewOptionStateEnum>(ViewOptionStateEnum.Default);
    const [appSettings, dispatchAppSettings] = useAppSettings();

    const formRef = useRef<ManualEntryFormRefType>(undefined);

    const [formState, setFormState] = useState<ManualEntryFormStateType>({
        isDirty: false,
        isValid: true
    });

    const { t } = useTranslation();
    const { confirm
    //    , acknowledge 
    } = useConfirmation();


    async function onCancel() {

        const canCancel = formState.isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ reason: "cancel" });
        }
    }


    function onSave() {

        const newValues = formRef.current?.getChanges() ?? [];
        const tags = formRef.current?.getTags() ?? [];

        onClose && onClose({
            reason: 'save',
            values: {
                newValues,
                tags
            }
        });


    }
    const getUserConfirmation = async () => {

        const confirmDialogValues = {
            dialogTitle: t('cancelConfirmation.title'),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        };

        const result = await confirm(confirmDialogValues);
        return result === "yes";
    };

    function onFormStateChanged(data: ManualEntryFormStateType) {
        setFormState(data);
    }

    return (

        open ?

        <FullScreenDialog open={open} onClose={reason => onClose({ reason: reason })}>

            <SettingsBar title={t('livedata.writedownTitle', {
                name: values?.objectName
            })} 
                onClose={
                    async (reason: SettingsActionEnum) => {
                        reason === SettingsActionEnum.OK
                            ? onSave()
                            : onCancel();
                    }}
                
                modified={formState.isDirty && formState.isValid} 
                hideAction={true}


                actions={
                    <Stack direction='row' spacing={1}>

                        <Tooltip title='View Options'>
                            <IconButton size="large"
                                aria-label="options"
                                color={viewOptionState === ViewOptionStateEnum.Changed ? 'secondary' : "inherit"}
                                edge='end'
                                sx={{ marginLeft: 'auto'}}
                                onClick={() => {
                                    dispatchAppSettings({
                                        type: AppSettingsActionTypeEnum.SideSheetOpenChanged,
                                        open: !appSettings.sideSheetOpen
                                    });
                            
                                   //setViewOptionState
                                }}>
                                <Tune />
                            </IconButton>
                        </Tooltip>

                        <Button autoFocus 
                            color= {formState.isDirty ? "secondary" : 'inherit' }
                            arial-label='save' 
                            disabled={!formState.isDirty} 
                            sx={{
                                color: formState.isDirty ? undefined : 'rgba(255,255,255,.4)!important',
                                opacity: formState.isDirty ? undefined : '.6!important'
                            }}
                            onClick={() => onSave()}>
                                {t('buttons.save')}
                        </Button>


                </Stack>
                } 

            />

                {isPhone ?
                <ManualEntryForm 
                    mode={ManualEntryFormMode.WriteDown} 
                    ref={formRef}
                    date={undefined} 
                    formState={formState} 
                    onFormStateChanged={onFormStateChanged} 
                    writedownMode={values?.writedownMode}
                    configurationID={values?.configurationID}
                    setViewOptionState={setViewOptionState}
                    elynxObject={values?.elynxObject}
                    filteredTags={values?.tags}
                /> :
                <DesktopManualEntryForm
                    mode={ManualEntryFormMode.WriteDown} 
                    ref={formRef}
                    date={undefined} 
                    formState={formState} 
                    onFormStateChanged={onFormStateChanged} 
                    writedownMode={values?.writedownMode}
                    configurationID={values?.configurationID}
                    setViewOptionState={setViewOptionState}
                    elynxObject={values?.elynxObject}
                    filteredTags={values?.tags}
                />
                }

                <UnsavedChangesPrompt
                        hasChanges={formState.isDirty}
                        getUserConfirmation={getUserConfirmation}
                    /> 

        </FullScreenDialog>
        : 
        null
    );
  }

  
export type WriteDownFormDialogState = DialogState<WriteDownFormDialogInitValues, WriteDownFormDialogResult>;
export type BindWriteDownFormDialogReturn = BindDialogReturn<WriteDownFormDialogInitValues, WriteDownFormDialogResult>;

export function useWriteDownFormDialogState(): WriteDownFormDialogState {
    return useDialogState();
}

export function bindWriteDownFormDialog(props: WriteDownFormDialogState): BindWriteDownFormDialogReturn {
    return bindDialog(props);
}
